// src/components/finance/RenewalDecisionsPanel.tsx

import React from 'react';
import {
  AlertTriangle,
  Calendar,
  RefreshCw,
  ArrowRight,
  LogOut,
  HeartPulse,
  Package
} from 'lucide-react';
import { RenewalDecisionAction, RenewalDecisionTask } from '../../services/RenewalDecisionService';

interface RenewalDecisionsPanelProps {
  decisions: RenewalDecisionTask[];
  onAction: (task: RenewalDecisionTask, action: RenewalDecisionAction) => void;
  disabled?: boolean;
}

const actionConfig: Array<{
  action: RenewalDecisionAction;
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  style: string;
}> = [
  {
    action: 'renew',
    label: 'Renovar Pacote',
    description: 'Iniciar novo ciclo de pacote',
    icon: RefreshCw,
    style: 'bg-blue-50 text-blue-700 hover:bg-blue-100'
  },
  {
    action: 'individual_sessions',
    label: 'Manter Avulso',
    description: 'Continuar com sessões avulsas',
    icon: ArrowRight,
    style: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
  },
  {
    action: 'discharge',
    label: 'Dar Alta',
    description: 'Encerrar atendimento do paciente',
    icon: LogOut,
    style: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
  },
  {
    action: 'deceased',
    label: 'Registrar Óbito',
    description: 'Atualizar informações de óbito',
    icon: HeartPulse,
    style: 'bg-rose-50 text-rose-700 hover:bg-rose-100'
  }
];

function formatDate(dateIso?: string) {
  if (!dateIso) return '--';
  const date = new Date(dateIso);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('pt-BR');
}

export default function RenewalDecisionsPanel({ decisions, onAction, disabled }: RenewalDecisionsPanelProps) {
  if (decisions.length === 0) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-6 text-center text-gray-500">
        <Calendar className="w-10 h-10 mx-auto mb-3 opacity-60" />
        <p>Nenhuma decisão de renovação pendente</p>
        <p className="text-sm">Pacotes atualizados e ciclos em dia</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <div>
            <h3 className="text-lg font-semibold">Decisões de Renovação</h3>
            <p className="text-xs text-gray-500">
              {decisions.length} pacote(s) aguardando decisão administrativa
            </p>
          </div>
        </div>
      </div>

      <div className="divide-y">
        {decisions.map(task => {
          const { patient, decision } = task;
          const snapshot = decision.packageSnapshot;

          return (
            <div key={decision.decisionId} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-gray-900">{patient.personalData.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                      Pendente desde {formatDate(decision.createdAt)}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>
                        Período:{' '}
                        <strong>
                          {formatDate(decision.periodStart)} a {formatDate(decision.periodEnd)}
                        </strong>
                      </span>
                    </div>
                    {snapshot && (
                      <>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span>{snapshot.packageName || 'Pacote atual'}</span>
                        </div>
                        <div>
                          <span className="block">
                            Sessões: <strong>{snapshot.sessionsUsed ?? 0}</strong> utilizadas /{' '}
                            <strong>{snapshot.sessionsContracted ?? '--'}</strong> contratadas
                          </span>
                          {snapshot.sessionsRemaining !== undefined && (
                            <span className="text-xs text-gray-500">
                              Restantes: {Math.max(0, snapshot.sessionsRemaining)}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
                {actionConfig.map(config => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={config.action}
                      onClick={() => !disabled && onAction(task, config.action)}
                      disabled={disabled}
                      className={`flex items-start gap-2 rounded-lg px-3 py-2 text-left transition ${
                        disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400' : config.style
                      }`}
                    >
                      <Icon className="w-4 h-4 mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold">{config.label}</div>
                        <div className="text-xs opacity-80">{config.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
